import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Mail, Loader2, Eye, Send, X, ArrowLeft, Search, Users, BookUser, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TypeBadge = ({ type }) => (
  type === 'member' ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-semibold">Member</span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold">Contact</span>
  )
);

export const OutreachComposer = ({ getPassword }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [previewHtml, setPreviewHtml] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const fetchRecipients = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/outreach/recipients?password=${encodeURIComponent(getPassword())}`);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [getPassword]);

  useEffect(() => { fetchRecipients(); }, [fetchRecipients]);

  const recipients = data?.recipients || [];

  const visible = useMemo(() => {
    let list = recipients;
    if (filter !== 'all') list = list.filter((r) => r.type === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((r) => r.name.toLowerCase().includes(q) || r.email.toLowerCase().includes(q));
    }
    return list;
  }, [recipients, filter, search]);

  const allVisibleSelected = visible.length > 0 && visible.every((r) => selectedKeys.includes(r.key));
  const allSelected = recipients.length > 0 && selectedKeys.length === recipients.length;

  const toggleKey = (key) => {
    setSelectedKeys((prev) => prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]);
  };

  const toggleAllVisible = () => {
    if (allVisibleSelected) {
      const visKeys = new Set(visible.map((r) => r.key));
      setSelectedKeys((prev) => prev.filter((k) => !visKeys.has(k)));
    } else {
      setSelectedKeys((prev) => [...new Set([...prev, ...visible.map((r) => r.key)])]);
    }
  };

  const handlePreview = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setPreviewLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/outreach/preview?password=${encodeURIComponent(getPassword())}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, message, recipient_keys: selectedKeys }),
        }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Preview failed');
      setPreviewHtml(d.html);
    } catch (e) {
      toast.error(e.message || 'Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSend = async () => {
    if (!subject.trim() || !message.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    if (selectedKeys.length === 0) {
      toast.error('Select at least one recipient');
      return;
    }
    const scope = allSelected
      ? `ALL ${recipients.length} recipients (members + contact list)`
      : `${selectedKeys.length} selected recipient(s)`;
    if (!window.confirm(`Send this email to ${scope}?`)) return;
    setSending(true);
    try {
      const res = await fetch(
        `${API_URL}/api/admin/outreach/send?password=${encodeURIComponent(getPassword())}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, message, recipient_keys: selectedKeys, send_all: allSelected }),
        }
      );
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Send failed');
      toast.success(`Email sent to ${d.sent} of ${d.total} recipient(s)${d.failed ? ` · ${d.failed} failed` : ''}`);
      if (d.failed && d.error) toast.error(`Reason for failures: ${d.error}`);
      setPreviewHtml(null);
      setSubject('');
      setMessage('');
      setSelectedKeys([]);
    } catch (e) {
      toast.error(e.message || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 animate-spin text-brand-navy" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-outreach-tab">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-navy-900 mb-2">Email Outreach</h2>
          <p className="text-navy-500 max-w-2xl">
            Draft and send a branded message to registered members and your master contact list.
            Select recipients individually or choose select all, and preview before sending.
          </p>
        </div>
        <Button
          onClick={fetchRecipients}
          variant="outline"
          className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
          data-testid="outreach-refresh"
        >
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 max-w-2xl">
        <Card className="bg-gradient-to-br from-brand-navy to-navy-700 text-white">
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-navy-200 mb-1">Total Reachable</p>
            <p className="text-3xl font-bold" data-testid="outreach-total">{data?.total ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-navy-500 mb-1 flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Members</p>
            <p className="text-3xl font-bold text-green-700" data-testid="outreach-member-count">{data?.member_count ?? 0}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-navy-500 mb-1 flex items-center gap-1"><BookUser className="w-3.5 h-3.5" /> Contact List</p>
            <p className="text-3xl font-bold text-amber-600" data-testid="outreach-contact-count">{data?.contact_count ?? 0}</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Recipients panel */}
        <Card className="lg:col-span-2">
          <CardContent className="p-0 flex flex-col max-h-[640px]">
            <div className="p-4 border-b border-navy-100 space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm font-semibold text-navy-800 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleAllVisible}
                    className="w-4 h-4 accent-[#0A2540]"
                    data-testid="outreach-select-all"
                  />
                  Select All{filter !== 'all' || search ? ' (filtered)' : ''} ({visible.length})
                </label>
                <span className="text-xs font-semibold px-2 py-1 rounded-full bg-navy-50 text-navy-700" data-testid="outreach-selected-count">
                  {selectedKeys.length} selected
                </span>
              </div>
              <div className="flex gap-1.5">
                {[
                  { id: 'all', label: `All (${recipients.length})` },
                  { id: 'member', label: `Members (${data?.member_count ?? 0})` },
                  { id: 'contact', label: `Contacts (${data?.contact_count ?? 0})` },
                ].map((f) => (
                  <button
                    key={f.id}
                    onClick={() => setFilter(f.id)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${filter === f.id ? 'bg-brand-navy text-white' : 'bg-navy-50 text-navy-600 hover:bg-navy-100'}`}
                    data-testid={`outreach-filter-${f.id}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-navy-400" />
                <Input
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9 h-9"
                  data-testid="outreach-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-auto p-2" data-testid="outreach-recipient-list">
              {visible.length === 0 ? (
                <p className="p-4 text-sm text-navy-400">No recipients match.</p>
              ) : (
                visible.map((r) => (
                  <label
                    key={r.key}
                    className={`flex items-start gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${selectedKeys.includes(r.key) ? 'bg-green-50' : 'hover:bg-navy-50'}`}
                    data-testid={`outreach-item-${r.key}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedKeys.includes(r.key)}
                      onChange={() => toggleKey(r.key)}
                      className="w-4 h-4 mt-0.5 accent-[#0A2540]"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        <span className="text-sm font-medium text-navy-900 truncate">{r.name}</span>
                        <TypeBadge type={r.type} />
                      </span>
                      <span className="block text-xs text-navy-500 truncate">{r.email}</span>
                    </span>
                  </label>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Compose panel */}
        <Card className="lg:col-span-3">
          <CardContent className="p-5 space-y-4">
            <div>
              <label className="text-sm font-semibold text-navy-800 mb-1 block">Subject</label>
              <Input
                placeholder="e.g., An Update from DC Men in ECE"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="outreach-subject"
              />
            </div>
            <div>
              <label className="text-sm font-semibold text-navy-800 mb-1 block">Message</label>
              <Textarea
                placeholder={"Write your message here…\n\nSeparate paragraphs with a blank line. Start with your own greeting (e.g., Dear Members,)."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={14}
                data-testid="outreach-message"
              />
              <p className="text-xs text-navy-400 mt-1.5">
                The email is wrapped in the DC Men in ECE branded template with your subject in the header and contact info in the footer.
                Links are clickable automatically — paste any URL, or use <code className="text-navy-600 bg-navy-50 px-1 rounded">[Link Text](https://example.com)</code> for custom link text.
              </p>
            </div>
            <div className="flex justify-end items-center gap-3 flex-wrap pt-2 border-t border-navy-100">
              <Button variant="outline" onClick={handlePreview}
                disabled={previewLoading || !subject.trim() || !message.trim()}
                className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                data-testid="outreach-preview-btn">
                {previewLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Loading…</> : <><Eye className="w-4 h-4 mr-2" /> Preview Email</>}
              </Button>
              <Button onClick={handleSend}
                disabled={sending || selectedKeys.length === 0 || !subject.trim() || !message.trim()}
                className="bg-brand-green hover:bg-green-700 text-white font-semibold"
                data-testid="outreach-send-btn">
                {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send to {selectedKeys.length}</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Preview modal */}
      {previewHtml && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="outreach-preview-modal">
          <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col">
            <div className="bg-brand-navy text-white p-5 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold">Email Preview</h2>
                <p className="text-white/70 text-sm">This is exactly how the email will look in the recipient's inbox.</p>
              </div>
              <button onClick={() => setPreviewHtml(null)} className="text-white/80 hover:text-white" data-testid="outreach-preview-close">
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-auto bg-slate-100 p-4">
              <iframe
                title="Email preview"
                srcDoc={previewHtml}
                className="w-full bg-white rounded-xl border border-navy-100"
                style={{ minHeight: '60vh' }}
                data-testid="outreach-preview-frame"
              />
            </div>
            <div className="border-t border-navy-100 p-4 flex justify-between items-center gap-3 flex-wrap">
              <Button variant="outline" onClick={() => setPreviewHtml(null)}
                className="border-2 border-navy-200 text-navy-700 hover:bg-navy-50"
                data-testid="outreach-back-to-edit">
                <ArrowLeft className="w-4 h-4 mr-2" /> Back to Edit
              </Button>
              <div className="flex items-center gap-3">
                <span className="text-sm text-navy-500">
                  Sending to <strong className="text-navy-900">{selectedKeys.length}</strong> recipient(s)
                </span>
                <Button onClick={handleSend} disabled={sending || selectedKeys.length === 0}
                  className="bg-brand-green hover:bg-green-700 text-white font-semibold"
                  data-testid="outreach-modal-send-btn">
                  {sending ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…</> : <><Send className="w-4 h-4 mr-2" /> Send Email</>}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutreachComposer;
